/*
 *
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * This code may be used, reproduced, modified, or distributed ONLY with the
 * prior written permission of Aegis AO Soft LLC / Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 *
 */


import React from 'react';
import { Loader2, AlertCircle } from 'lucide-react';

/** Minimal shape shared by the toll vehicle/transaction scan DTOs (and any future background scan). */
export interface ScanProgress {
  status: 'processing' | 'completed' | 'error' | 'not_found';
  progress: number;
  step?: string;
  error?: string | null;
}

interface ScanProgressBarProps {
  scan: ScanProgress | null | undefined;
  /** Shown while processing when the scan hasn't reported a step label yet. */
  fallbackLabel?: string;
  /** Render the red error box when status === 'error'. Set false when the page surfaces the error itself. */
  showError?: boolean;
  className?: string;
}

/**
 * Progress bar for a polled background scan (vehicles/transponders, transactions, …). Renders a blue
 * bar with the current step + percent while processing, an optional red box on error, nothing otherwise.
 */
export const ScanProgressBar: React.FC<ScanProgressBarProps> = ({
  scan,
  fallbackLabel = 'Loading…',
  showError = true,
  className,
}) => {
  if (!scan) return null;

  if (scan.status === 'processing') {
    return (
      <div className={`rounded-lg border border-blue-200 bg-blue-50 p-3 ${className ?? ''}`}>
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-2 text-sm text-blue-700">
            <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
            <span>{scan.step || fallbackLabel}</span>
          </div>
          <span className="text-xs text-blue-600 tabular-nums">{scan.progress}%</span>
        </div>
        <div className="h-1.5 w-full bg-blue-100 rounded-full overflow-hidden">
          <div className="h-full bg-blue-500 transition-all duration-500" style={{ width: `${scan.progress}%` }} />
        </div>
      </div>
    );
  }

  if (scan.status === 'error' && showError) {
    return (
      <div className={`rounded-lg border border-red-200 bg-red-50 p-3 flex items-center gap-2 ${className ?? ''}`}>
        <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
        <span className="text-sm text-red-700">{scan.error ? `Load failed: ${scan.error}` : 'Load failed'}</span>
      </div>
    );
  }

  return null;
};

export default ScanProgressBar;
